'use client';

import React from 'react';
import { Finance, Interfaces } from 'doodle-icons';
import StationeryCapabilityBoard, { type StationeryCapabilityPanel } from '@/components/common/StationeryCapabilityBoard';
import { useGsapReveal } from '@/hooks/useGsapReveal';

const PANELS: StationeryCapabilityPanel[] = [
  {
    eyebrow: 'Diagnose',
    title: 'See the exact concept that breaks.',
    description: 'IntelGrader turns checked work into a structured map of mastery, formulas, and hidden misconceptions.',
    icon: <Finance.TrendUp width={22} height={22} fill="currentColor" />,
    accent: 'blue',
    rotation: '-1.1deg',
    points: [
      {
        label: 'Concept Mastery Tracking',
        detail: 'Track which nodes each student owns and which ones keep slipping.',
        icon: <Finance.TrendUp width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Formula-Level Diagnostics',
        detail: 'Pinpoint the exact step or formula transition where the answer broke.',
        icon: <Interfaces.Search width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Concept Gap Detection',
        detail: 'Surface recurring blind spots across one student, one batch, or a whole class.',
        icon: <Interfaces.Caution width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Review',
    title: 'Make feedback feel placed, not appended.',
    description: 'The review layer stays spatial and legible, so students immediately see what changed and why.',
    icon: <Interfaces.Target width={22} height={22} fill="currentColor" />,
    accent: 'green',
    rotation: '0.9deg',
    points: [
      {
        label: 'Ghost Corrections',
        detail: 'Correct working appears directly over the original page instead of on a separate sheet.',
        icon: <Interfaces.Unhide width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Spatial Precision Feedback',
        detail: 'Corrections land exactly where the student made the mistake.',
        icon: <Interfaces.Target width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'MASTER AUDIT Review',
        detail: 'Confidence-tiered review keeps the teacher in control without forcing manual checking of everything.',
        icon: <Interfaces.Shield width={18} height={18} fill="currentColor" />,
      },
    ],
  },
  {
    eyebrow: 'Act',
    title: 'Turn grading into intervention and communication.',
    description: 'Every checked paper becomes next-step intelligence for teachers, students, and parents.',
    icon: <Interfaces.Checklist width={22} height={22} fill="currentColor" />,
    accent: 'amber',
    rotation: '-0.4deg',
    size: 'wide',
    points: [
      {
        label: 'Intervention Cohorts',
        detail: 'Group students with similar gaps so remediation is targeted instead of generic.',
        icon: <Interfaces.User width={18} height={18} fill="currentColor" />,
      },
      {
        label: 'Parent Intelligence Reports',
        detail: 'Parents see concept mastery and progress patterns, not just marks.',
        icon: <Interfaces.Doc width={18} height={18} fill="currentColor" />,
      },
    ],
  },
];

const Features: React.FC = () => {
  const headerRef = useGsapReveal<HTMLDivElement>({ y: 30, duration: 0.7 });
  const boardRef = useGsapReveal<HTMLDivElement>({ y: 50, duration: 0.9, start: 'top 80%' });

  return (
    <section id="features" className="features-section py-24 px-6 relative overflow-hidden scroll-mt-32">
      <div className="max-w-[1250px] mx-auto">
        <div ref={headerRef} className="section-header section-header--secondary section-header--capabilities">
          <span className="section-eyebrow">Capabilities</span>
          <h2 className="section-title">Diagnose gaps. Review precisely. Drive intervention.</h2>
          <p className="section-subtitle">IntelGrader turns checked work into concept-level diagnostics, spatial feedback, and the next teaching move.</p>
        </div>

        <div ref={boardRef}>
          <StationeryCapabilityBoard panels={PANELS} variant="editorial" />
        </div>
      </div>
    </section>
  );
};

export default Features;
